import { cn } from '../../../lib/utils'

interface ClozeAnswerCardProps {
  blanks: Array<{
    index: number
    questionId: string
    value: string
    isCorrect?: boolean | null
    correctAnswer?: string | null
  }>
  activeBlank: number | null
  onBlankClick: (index: number) => void
  onGradeAll?: () => void
  isGrading?: boolean
  readOnly?: boolean
}

/**
 * Bottom answer card for cloze questions.
 * Shows all blank numbers with answer status.
 */
export function ClozeAnswerCard({
  blanks,
  activeBlank,
  onBlankClick,
  onGradeAll,
  isGrading,
  readOnly,
}: ClozeAnswerCardProps) {
  const answeredCount = blanks.filter(b => b.value).length

  return (
    <div className="border-t border-slate-200 pt-3 mt-4">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs text-slate-500">
          答题卡 · 已答 {answeredCount}/{blanks.length}
        </span>
        {onGradeAll && (
          <button
            type="button"
            onClick={onGradeAll}
            disabled={isGrading || readOnly}
            className={cn(
              'text-xs px-3 py-1 rounded-md font-medium transition-colors',
              isGrading || readOnly
                ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                : 'bg-indigo-50 text-indigo-600 hover:bg-indigo-100'
            )}
          >
            {isGrading ? '批改中...' : '全部批改'}
          </button>
        )}
      </div>

      <div className="flex flex-wrap gap-1.5">
        {blanks.map((blank) => {
          const isActive = blank.index === activeBlank
          const isAnswered = !!blank.value

          // Color scheme
          let bg = 'bg-white border-slate-200 text-slate-400' // unanswered
          if (isAnswered) bg = 'bg-indigo-50 border-indigo-300 text-indigo-700' // answered
          if (blank.isCorrect === true) bg = 'bg-green-50 border-green-400 text-green-700' // correct
          if (blank.isCorrect === false) bg = 'bg-red-50 border-red-400 text-red-700' // wrong
          if (isActive) bg += ' ring-2 ring-indigo-400 ring-offset-1' // active

          return (
            <button
              key={blank.index}
              type="button"
              onClick={() => onBlankClick(blank.index)}
              className={[
                'inline-flex items-center justify-center w-9 h-8 rounded border text-xs font-medium transition-all',
                bg,
              ].join(' ')}
              title={
                blank.isCorrect === false
                  ? `正确答案: ${blank.correctAnswer || '?'}`
                  : blank.isCorrect === true
                    ? '正确'
                    : undefined
              }
            >
              {isAnswered ? blank.value : blank.index}
            </button>
          )
        })}
      </div>
    </div>
  )
}
